/*
 * author: Michael Bauer (Geoplex)
 * email:  michael.bauer@geoplex.com.au
 * 
 */

import ESRIConfig from "@arcgis/core/config"
import { addProxyRule } from "@arcgis/core/core/urlUtils"
import Map from "@arcgis/core/Map"
import MapView from "@arcgis/core/views/MapView"
import SceneView from "@arcgis/core/views/SceneView"
import Widget from "@arcgis/core/widgets/Widget"
import * as watchUtils from "@arcgis/core/core/watchUtils"

import Mouse from "dojo/mouse"

import { presetConfigs, Config, applyConfig, loadLocalConfig, getLocalConfigList } from "./config"
import { getLayers, getCustomBaseMaps, getLayerIdsWhere, initLayers, getLayer, loadIncidents, get_planet_explorer_link } from "./layers"
import TimeSlider from "./timeslider"
import { buildWidget, returnToPanel, widgets } from './widgetbuilder'

interface Widgets {
    [key: string]: Widget
}

export interface Application {
  config: Config,
  initialExtent: any,
  viewPadding: any,
  uiPadding: any,
  mapView: MapView | null,
  sceneView: SceneView | null,
  activeView: MapView | SceneView | null,
  searchWidgetNav: any,
  screenWidth: number,
  screenHeight: number,
}

let defaultAppConfig : Application = {
  config: presetConfigs.default,
  initialExtent: null,
  viewPadding: {
    top: 50, bottom: 50
  },
  uiPadding: {
    top: 15, bottom: 15
  },
  mapView: null,
  sceneView: null,
  activeView: null,
  searchWidgetNav: null,
  screenWidth: 0,
  screenHeight: 0,
};

export let appConfig: Application = defaultAppConfig

let popupActionSwitch = 'zoomTo'

let copyAction: any = {
    title: `Copy to Clipboard`,
    id: "copy-to-clipboard",
    className: 'esri-icon-duplicate'
}

let zoomToAction: any = null
let refreshTimer: NodeJS.Timer

let lastInvalidateTime: number = 0;
let timeBetweenInvalidation: number = 5 * 60 * 1000 // 5 minutes
let casualRefreshTime: number = 2.5 * 60 * 1000 // 2.5 minute


// Will force the main timeslider to update
export function refreshTimeSlider() {
    // @ts-ignore
    widgets['timeslider_widget'].updateLayers()
}

// will refresh all visible layers and after a specific time period (timeBetweenInvalidation)
// will ensure all layers have been refreshed with metadata removed
export function refreshLayers(forceInvalidate: boolean = false) {
    console.log("Refreshing Layers")
    let do_refreshTimeSlider: boolean = false; 
    for (let key in getLayers()) {

        if (forceInvalidate || Date.now() - lastInvalidateTime > timeBetweenInvalidation) {
            getLayer(key)?.invalidateCache()
            do_refreshTimeSlider = true
            console.log("Invalidated Layer Cache")
        }

        // @ts-ignore
        if (getLayer(key).esriLayer && getLayer(key).esriLayer.visible && getLayer(key).esriLayer.refresh && getLayer(key).allowRefresh) {
            getLayer(key)?.invalidateCache(true)
            // @ts-ignore
            getLayer(key).esriLayer.refresh()
            do_refreshTimeSlider = true
        } 
    }
    if (forceInvalidate || Date.now() - lastInvalidateTime > timeBetweenInvalidation) {
        lastInvalidateTime = Date.now()
    }

    loadIncidents(true)

    if (do_refreshTimeSlider) {
        refreshTimeSlider()
    }
}

// Will force all layers to have their metadata to be invalidated
export function refreshLayersForceInvalidate() {
    refreshLayers(true)
}

// Displays a warning with the given title and text
export function showWarning(title: string, text: string) {
    $('#warning-location').html(`
        <div class="calcite-alert alert alert-warning alert-dismissible" role="alert">
            <button type="button" class="close" data-dismiss="alert" aria-label="Close">
                <span aria-hidden="true">&times;</span>
            </button>
            <div class="alert-left"><span class="esri-icon-notice-round"></span> ${title} </div>
            <div class="alert-message"> ${text}</div>
        </div>
    `)
}


export function setUpdating(evt: boolean) {
    if (evt === true) {
        $("[name=is-updating-layers]").addClass('spin');
    } else {
        $("[name=is-updating-layers]").removeClass('spin');
    }
}

// Initializes the Application
export function init() {

    const searchParams = new URLSearchParams(window.location.search)

    // the default config to load
    let configId = 'Default'

    // first get the list of configs
    getLocalConfigList().then((configs) => {
        // if another config exists in the URL params (eg: config=Storm Monitoring) then set that.
        const configName = searchParams.get('config')
        if (configName && configs.indexOf(configName) > -1) {
            configId = configName
        }

        // load the config
        loadLocalConfig(configId).then((initConfig) => {

            appConfig.config = initConfig


            // Set proxy and CORS rules

            //ESRIConfig.request.proxyUrl = '/proxy/proxy.ashx'

            // const proxyUrl = 'https://fireweather.uat.rfs.nsw.gov.au/proxy/proxy.ashx'
            // const proxyUrl = 'http://localhost:55272/proxy/proxy.ashx'

            // addProxyRule({
            //     urlPrefix: 'https://reg.bom.gov.au',
            //     proxyUrl,
            // })

            // addProxyRule({
            //     urlPrefix: 'https://spatial.bom.gov.au',
            //     proxyUrl,
            // })

            // addProxyRule({
            //     urlPrefix: 'https://api.planet.com',
            //     proxyUrl,
            // })

            // addProxyRule({
            //     urlPrefix: 'https://tiles.planet.com',
            //     proxyUrl,
            // })

            // addProxyRule({
            //     urlPrefix: 'http://www.rfs.nsw.gov.au',
            //     proxyUrl,
            // })

            // addProxyRule({
            //     urlPrefix: 'http://spatial.rfs.nsw.gov.au',
            //     proxyUrl,
            // })

            // @ts-ignore
            ESRIConfig.request.corsDetection = false;
            // @ts-ignore
            ESRIConfig.request.corsEnabledServers.push("nsw.gov.au")
            // @ts-ignore
            // ESRIConfig.request.alwaysUseProxy = true;


            // Initialize 2D and 3D Maps and MapViews

            // Map 
            let map = new Map({
                basemap: appConfig.config.basemap,
            });


            appConfig.mapView = new MapView({
                container: "mapViewDiv",
                map: map,
                scale: appConfig.config.initextent.scale,
                center: appConfig.config.initextent.center,
                padding: appConfig.viewPadding,
                ui: {
                    components: ["zoom", "compass", "attribution"],
                    padding: appConfig.uiPadding
                }
            });

            appConfig.mapView.when(function () {
                if(appConfig.mapView)
                    appConfig.mapView.popup.dockOptions.position = "top-right";
            });

            appConfig.mapView.watch('updating', setUpdating)



            // Scene
            let mapScene = new Map({
                basemap: appConfig.config.basemap,
                ground: "world-elevation"
            });

            appConfig.sceneView = new SceneView({
                container: "sceneViewDiv",
                map: mapScene,
                scale: appConfig.config.initextent.scale,
                center: appConfig.config.initextent.center,
                padding: appConfig.viewPadding,
                ui: {
                    padding: appConfig.uiPadding
                }
            });

            appConfig.sceneView.when(function () {
                if(appConfig.sceneView)
                    appConfig.sceneView.popup.dockOptions.position = "top-right";
            });

            appConfig.sceneView.watch('updating', setUpdating)


            // Handles the dynamic switching of the maps Popup between the standard click details
            // for displaying feature data, and the special popup displaying the long, lat and 
            // temperature of the location
            function onMapRightClickEvent(event: __esri.ViewClickEvent) {
                const activeView = appConfig.activeView
                if(!activeView) return;

                if (zoomToAction === null) {
                    // @ts-ignore
                    zoomToAction = appConfig.activeView.popup.actions.items[0]
                    activeView.popup.actions.removeAll()
                }

                if (event.button === 2) {
                    let lat = Math.round(event.mapPoint.latitude * 1000) / 1000;
                    let lon = Math.round(event.mapPoint.longitude * 1000) / 1000;

                    if (popupActionSwitch == 'zoomTo') {
                        activeView.popup.actions.removeAll()
                        activeView.popup.actions.add(copyAction)
                        popupActionSwitch = 'copy'
                    }
                    
                    // @ts-ignore
                    appConfig.activeView.popup.actions.items[0].title = 'Copy to Clipboard' 

                    activeView.popup.autoOpenEnabled = false;
                    activeView.popup.open({
                        title: "Selected Location",
                        location: event.mapPoint,
                        content: `
                            <p>Longitude: ${lon}, Latutide: ${lat} </p>
                            <textarea id="text-to-copy" style="position: absolute; left: -99999px" readonly>[${lon}, ${lat}]</textarea>
                        `,
                    });

                    // <a href="`+ get_planet_explorer_link(event.mapPoint, appConfig.activeView.zoom) +`" target="_blank">View Location in Planet Explorer (Planet Login Required)</a>


                    getLayer('Himawari_Band7')?.identify(lat, lon).then((results) => {
                        const val = results.value=="NoData" ? results.properties.Values[0] : results.value
                        activeView.popup.content = `
                            <p>Temperature: ${Math.floor((val - 273.15) * 100) / 100} <sup>o</sup>C</p>
                        ` + activeView.popup.content 
                    }, (err) => {
                        console.log("Identify Error")
                        console.log(err)
                        activeView.popup.content = `
                            <p>Unable to Query Temperature</p>
                        ` + activeView.popup.content 
                        
                    })

                    activeView.popup.autoOpenEnabled = true;

                } else {
                    // Left click

                    if (popupActionSwitch == 'copy') {
                        activeView.popup.actions.removeAll()
                        activeView.popup.actions.add(zoomToAction)
                        popupActionSwitch = 'zoomTo'
                    }
                }
            }

            // A function to be called upon the click of a popup action, handles copy-to-clipboard action.
            function trigger_action(event: any) {
                // Execute the measureThis() function if the measure-this action is clicked
                if (event.action.id === "copy-to-clipboard") {
                    const text_to_copy = $('#text-to-copy')?.text()
                    if(text_to_copy) {
                        navigator.clipboard.writeText(text_to_copy).then(() => {
                            // @ts-ignore
                            appConfig.activeView.popup.actions.items[0].title = 'Copied!' 
                        }, () => {
                            // @ts-ignore
                            appConfig.activeView.popup.actions.items[0].title = 'Failed to Copy' 
                        })
                        
                    }
                        
                }
            }

            // Set triggers
            appConfig.mapView.popup.on("trigger-action", trigger_action);
            appConfig.sceneView.popup.on("trigger-action", trigger_action);

            appConfig.mapView.on("click", onMapRightClickEvent);
            appConfig.sceneView.on("click", onMapRightClickEvent);


            // Set the active view to be the 2D MapView
            appConfig.activeView = appConfig.mapView;
            appConfig.activeView.when(function () {
                appConfig.initialExtent = appConfig.activeView?.extent;
            });

            // Initialize and load all the layers
            initLayers().then((l) => {

                // apply other layer settings
                applyConfig(configId)



                // Handle UI Events

                // Views - Listen to view size changes to show/hide panels
                if(!appConfig.mapView || !appConfig.sceneView || !appConfig.activeView) return;

                const viewSizeChange = (screenSize: any) => {
                    if (appConfig.screenWidth !== screenSize[0]) {
                        appConfig.screenWidth = screenSize[0];
                        setPanelVisibility();
                    }
                }

                appConfig.mapView.watch("size", viewSizeChange);
                appConfig.sceneView.watch("size", viewSizeChange);


                


                // Panels - Show/hide the panel when popup is docked
                const setPanelVisibility = () => {
                    if(!appConfig.activeView) return;

                    let isMobileScreen = appConfig.activeView.widthBreakpoint === "xsmall" || appConfig.activeView.widthBreakpoint === "small",
                        isDockedVisible = appConfig.activeView.popup.visible && appConfig.activeView.popup.currentDockPosition,
                        isDockedBottom = appConfig.activeView.popup.currentDockPosition && appConfig.activeView.popup.currentDockPosition.indexOf("bottom") > -1;
                    // Mobile (xsmall/small)
                    if (isMobileScreen) {
                        if (isDockedVisible && isDockedBottom) {
                            $(".calcite-panels").addClass("invisible");
                        } else {
                            $(".calcite-panels").removeClass("invisible");
                        }
                    } else { // Desktop (medium+)
                        if (isDockedVisible) {
                            $(".calcite-panels").addClass("invisible");
                        } else {
                            $(".calcite-panels").removeClass("invisible");
                        }
                    }
                }

                // Popups - Listen to popup changes to show/hide panels
                appConfig.mapView.popup.watch(["visible", "currentDockPosition"], setPanelVisibility);
                appConfig.sceneView.popup.watch(["visible", "currentDockPosition"], setPanelVisibility);

                // Panels - Dock popup when panels show (desktop or mobile)
                $(".calcite-panels .panel").on("show.bs.collapse", function (e) {
                    if (appConfig.activeView?.popup.currentDockPosition || appConfig.activeView?.widthBreakpoint === "xsmall") {
                        appConfig.activeView.popup.dockEnabled = false;
                    }
                });

                // Panels - Undock popup when panels hide (mobile only)
                $(".calcite-panels .panel").on("hide.bs.collapse", function (e) {
                    if (appConfig.activeView?.widthBreakpoint === "xsmall") {
                        appConfig.activeView.popup.dockEnabled = true;
                    }
                });


                // Tab Events (Views)
                $(".calcite-navbar li a[data-toggle='tab']").on("click", function (e: any) {
                    syncTabs(e);
                    if(!appConfig.sceneView || !appConfig.mapView) return;

                    if (e.target.text.indexOf("Map") > -1) {
                        syncViews(appConfig.sceneView, appConfig.mapView);
                        appConfig.activeView = appConfig.mapView;
                    } else {
                        syncViews(appConfig.mapView, appConfig.sceneView);
                        appConfig.activeView = appConfig.sceneView;
                    }
                });

                // Tab
                function syncTabs(e: any) {
                    $(".calcite-navbar li.active").removeClass("active");
                    $(e.target).addClass("active");
                }

                // View
                function syncViews(fromView: MapView | SceneView, toView: MapView | SceneView) {
                    watchUtils.whenTrueOnce(toView, "ready").then(function (result) {
                        watchUtils.whenTrueOnce(toView, "stationary").then(function (result) {
                            toView.goTo(fromView.viewpoint);
                            toView.popup.reposition();
                        });
                    });
                    toView.map.addMany(fromView.map.layers.toArray())
                }


                // Basemap events
                $("#selectBasemapPanel").on("change", function (e: any) {
                    let basemap = getCustomBaseMaps(e.target.value)
                    if(!appConfig.activeView) return;

                    if (basemap) {
                        appConfig.activeView.map.basemap = basemap;
                    } else {
                        appConfig.activeView.map.basemap = e.target.options[e.target.selectedIndex].dataset.vector;
                    }
                });

                // Collapsible popup (optional)
                $(".esri-popup .esri-title").on("click", function (e) {
                    $(".esri-popup .esri-container").toggleClass("esri-popup-collapsed");
                    appConfig.activeView?.popup.reposition();
                });

                // Home
                $("#gotoNSWButton").on("click", function (e) {
                    appConfig.activeView?.goTo({ target: [147.723388, -32.763046], rotation: 0, scale: 2500000, });
                })


                // Handle layer refreshing, onclick and every 60 seconds
                $("[name=action-refresh-layers]").on('click', refreshLayersForceInvalidate)
                refreshTimer = setInterval(refreshLayers, casualRefreshTime)


                $("#goto-context-layers").on('click', (e) => {
                    // @ts-ignore
                    $("#dynamiclayers").collapse('hide');
                    // @ts-ignore
                    $('#collapsedynamiclayers').collapse('hide')

                    // @ts-ignore
                    $("#contextlayers").collapse('show');
                    // @ts-ignore
                    $("#collapsecontextlayers").collapse('show');
                })

                $("#goto-satellite-layers").on('click', (e) => {
                    // @ts-ignore
                    $("#contextlayers").collapse('hide');
                    // @ts-ignore
                    $('#collapsecontextlayers').collapse('hide')

                    // @ts-ignore
                    $("#dynamiclayers").collapse('show');
                    // @ts-ignore
                    $("#collapsedynamiclayers").collapse('show');
                })

                loadWidgets()

            })
        })
    })
}


// Init all the non-dynamically loaded widgets needed for the app.
function loadWidgets() {
    // (re)Load initial widgets

    $("#control-panel-return").on('click', (e) => {
        if (returnToPanel) {

            // @ts-ignore
            $("#layercontrols").collapse('hide');
            // @ts-ignore
            $('#collapsecontrols').collapse('hide')

            // @ts-ignore
            $("#" + returnToPanel).collapse('show');
            // @ts-ignore
            $("#collapse" + returnToPanel).collapse('show');
        }
    })

    buildWidget({
        id: 'timeslider_widget',
        type: 'TimeSlider',
        settings: {
            container: 'timeslider_widget',
            min: 0,
            max: 12,
            value: 11,
            step: 1,
            labelstep: 1,
            animatedelay: appConfig.config.timeSliderDelay || 10 * 1000,
            layers: [
                getLayer("Himawari_Composite1km"),
                getLayer('Himawari_Band7'),
                getLayer("Himawari_Zehr"),
                getLayer("Himawari_WaterVapour")
            ],
            context_layers: [
                getLayer('Himawari_MaskedTemp'),
            ]
        }
    })


    buildWidget({
        id: 'dynamic-layer-list',
        type: 'LayerSelector',
        settings: {
            container: 'dynamic-layer-list',
            allowMany: false,
            layerIds: getLayerIdsWhere('isContextual', false),
            panel: "dynamiclayers",
        }
    })


    buildWidget({
        id: 'context-layer-list',
        type: 'LayerSelector',
        settings: {
            allowMany: true,
            layerIds: getLayerIdsWhere('isContextual', true),
            container: "context-layer-list",
            panel: "contextlayers",
        }
    })

    buildWidget({
        id: 'presets-widget',
        type: 'PresetPanel',
        settings: {
            container: "presets-widget"
        }
    })

    buildWidget({
        id: 'timeslider-delay-widget',
        type: "ValueSlider",
        settings: {
            container: 'timeslider-delay-widget',
            min: 0.5,
            max: 30,
            step: 0.5,
            value: 10,
            labels: ['1s', '30s'],
            pretext: "TimeSlider Step Duration: ",
            posttext: "s",
            slideField: null,
            callback: (value: number) => {
                (widgets['timeslider_widget'] as unknown as TimeSlider).animatedelay = value*1000
            }
        }
    })

    buildWidget({
        id: 'refresh-rate-widget',
        type: "ValueSlider",
        settings: {
            container: 'refresh-rate-widget',
            min: 5,
            max: 600,
            step: 1,
            value: 150,
            labels: ['5s', '600s'],
            pretext: "Layer Refresh Rate: ",
            posttext: "s",
            slideField: null,
            callback: (value: number) => {
                casualRefreshTime = value * 1000
                timeBetweenInvalidation = 2 * casualRefreshTime

                if(refreshTimer != null)
                    clearInterval(refreshTimer)
                refreshTimer = setInterval(refreshLayers, casualRefreshTime)
            }
        }
    })
}




